import React, { useRef, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

/**
 * Xem và thay ảnh 2 mặt CCCD của nhân sự / ứng viên.
 *
 * Ảnh do ứng viên chụp lúc tự khai (service bhs-ung-vien), lưu trên FTP sau endpoint
 * download-image có yêu cầu auth => phải fetch kèm Bearer token rồi tạo object URL,
 * không đặt thẳng URL vào thẻ <img> được.
 *
 * Ảnh mới chọn được resize + nén tại client rồi gửi lên dạng data URL base64 — cùng
 * cơ chế với form ứng viên, backend đã có sẵn phần giải mã trong registry.php.
 */
export default function AnhCCCD({ formik, nameFront = "cccd_front", nameBack = "cccd_back", chophepSua = true }) {
    const { user } = useSelector((state) => state.auth);
    const [xemAnh, setXemAnh] = useState(null); // {url, nhan}
    const [dangXuLy, setDangXuLy] = useState("");
    const [loi, setLoi] = useState("");
    const frontRef = useRef(null);
    const backRef = useRef(null);

    const giaTri = { front: formik.values[nameFront] || "", back: formik.values[nameBack] || "" };

    /** Ảnh có thể là URL đã lưu trên server, hoặc data URL vừa chọn (chưa lưu). */
    const laDataUrl = (v) => typeof v === "string" && v.startsWith("data:");

    // Ảnh trên server nằm sau endpoint cần Bearer token nên không gắn thẳng URL vào <img>.
    // Prefetch về blob URL để hiển thị THUMBNAIL ngay (thay vì nút "Xem ảnh"). Ảnh vừa chọn
    // (data URL) hiển thị thẳng, không cần fetch. thumb[mat] = { src, dangTai, loi }.
    const [thumb, setThumb] = useState({ front: null, back: null });

    useEffect(() => {
        let huy = false;
        const taoUrls = []; // gom blob URL đã tạo để thu hồi khi cleanup

        const taiThumb = async (mat) => {
            const v = giaTri[mat];
            if (!v) { setThumb((t) => ({ ...t, [mat]: null })); return; }
            if (laDataUrl(v)) { setThumb((t) => ({ ...t, [mat]: { src: v } })); return; }
            setThumb((t) => ({ ...t, [mat]: { dangTai: true } }));
            try {
                const res = await fetch(v, { headers: user.config.headers });
                if (!res.ok) throw new Error("HTTP " + res.status);
                const blob = await res.blob();
                if (huy) return;
                const url = URL.createObjectURL(blob);
                taoUrls.push(url);
                setThumb((t) => ({ ...t, [mat]: { src: url } }));
            } catch (err) {
                if (!huy) setThumb((t) => ({ ...t, [mat]: { loi: err.message } }));
            }
        };

        taiThumb("front");
        taiThumb("back");

        return () => {
            huy = true;
            taoUrls.forEach((u) => URL.revokeObjectURL(u));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [giaTri.front, giaTri.back]);

    const moXem = async (mat) => {
        const v = giaTri[mat];
        if (!v) return;
        const nhan = mat === "front" ? "Mặt trước" : "Mặt sau";

        // Ảnh vừa chọn thì hiển thị thẳng, không cần tải lại.
        if (laDataUrl(v)) {
            setXemAnh({ url: v, nhan });
            return;
        }
        // Đã prefetch thumbnail (blob) rồi thì phóng to dùng luôn, khỏi fetch lại.
        if (thumb[mat]?.src) {
            setXemAnh({ url: thumb[mat].src, nhan, dungThumb: true });
            return;
        }
        setXemAnh({ url: "", nhan, dangTai: true });
        try {
            const res = await fetch(v, { headers: user.config.headers });
            if (!res.ok) throw new Error("HTTP " + res.status);
            const blob = await res.blob();
            setXemAnh({ url: URL.createObjectURL(blob), nhan });
        } catch (err) {
            setXemAnh({ url: "", nhan, loi: "Không tải được ảnh: " + err.message });
        }
    };

    const dongXem = () => {
        // Thu hồi object URL để không rò bộ nhớ khi mở/đóng nhiều lần.
        // LƯU Ý: khi phóng to dùng chung blob của thumbnail (dungThumb) thì KHÔNG revoke —
        // blob đó vẫn đang được thumbnail sử dụng, revoke sẽ làm hỏng ảnh nhỏ. useEffect cleanup
        // sẽ lo thu hồi các blob thumbnail.
        if (!xemAnh?.dungThumb && xemAnh?.url && xemAnh.url.startsWith("blob:")) URL.revokeObjectURL(xemAnh.url);
        setXemAnh(null);
    };

    /** Resize + nén trước khi gửi: ảnh điện thoại 3-8MB, base64 còn phình thêm ~33%. */
    const nenAnh = (file, maxSize = 1280, quality = 0.85) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error("Không đọc được file"));
            reader.onload = (ev) => {
                const img = new Image();
                img.onerror = () => reject(new Error("File ảnh bị lỗi hoặc không hỗ trợ"));
                img.onload = () => {
                    let { width, height } = img;
                    if (width > maxSize || height > maxSize) {
                        const r = Math.min(maxSize / width, maxSize / height);
                        width = Math.round(width * r);
                        height = Math.round(height * r);
                    }
                    const c = document.createElement("canvas");
                    c.width = width;
                    c.height = height;
                    const ctx = c.getContext("2d");
                    // Nền trắng: ảnh PNG trong suốt chuyển JPEG sẽ thành nền đen.
                    ctx.fillStyle = "#FFFFFF";
                    ctx.fillRect(0, 0, width, height);
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(c.toDataURL("image/jpeg", quality));
                };
                img.src = ev.target.result;
            };
            reader.readAsDataURL(file);
        });

    const chonAnh = async (mat, e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;

        setLoi("");
        setDangXuLy(mat);
        try {
            const dataUrl = await nenAnh(file);
            formik.setFieldValue(mat === "front" ? nameFront : nameBack, dataUrl);
        } catch (err) {
            setLoi("Không xử lý được ảnh: " + err.message);
        } finally {
            setDangXuLy("");
        }
    };

    const xoaAnh = (mat) => {
        formik.setFieldValue(mat === "front" ? nameFront : nameBack, "");
    };

    const renderO = (mat, nhan) => {
        const v = giaTri[mat];
        const th = thumb[mat];
        const ref = mat === "front" ? frontRef : backRef;
        return (
            <div className="col-6 col-sm-3 form-group">
                <label className="d-block mb-1">{nhan}</label>
                {v ? (
                    <div className="position-relative border rounded p-1 bg-white">
                        {/* Luôn hiển thị THUMBNAIL (ảnh server đã prefetch về blob, hoặc data URL
                            vừa chọn). Bấm vào ảnh để phóng to trong modal. */}
                        {th?.src ? (
                            <img
                                src={th.src}
                                alt={nhan}
                                className="w-100 rounded"
                                style={{ height: 90, objectFit: "cover", cursor: "zoom-in" }}
                                title="Bấm để phóng to"
                                onClick={() => moXem(mat)}
                            />
                        ) : th?.dangTai ? (
                            <div className="d-flex align-items-center justify-content-center text-primary"
                                style={{ height: 90 }}>
                                <span className="spinner-border spinner-border-sm"></span>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary w-100"
                                style={{ height: 90 }}
                                onClick={() => moXem(mat)}
                                title={th?.loi ? "Không tải được ảnh: " + th.loi : ""}
                            >
                                <span className="fas fa-id-card"></span>
                                <br />
                                Xem ảnh
                            </button>
                        )}
                        {chophepSua && (
                            <div className="d-flex mt-1" style={{ gap: 4 }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary flex-fill"
                                    style={{ fontSize: 11 }}
                                    onClick={() => ref.current.click()}
                                    disabled={dangXuLy === mat}
                                >
                                    {dangXuLy === mat ? "Đang xử lý..." : "Đổi ảnh"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ fontSize: 11 }}
                                    title="Xoá ảnh"
                                    onClick={() => xoaAnh(mat)}
                                >
                                    &times;
                                </button>
                            </div>
                        )}
                        {laDataUrl(v) && (
                            <div className="text-center text-warning" style={{ fontSize: 11 }}>
                                Ảnh mới — bấm Lưu để cập nhật
                            </div>
                        )}
                    </div>
                ) : chophepSua ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary w-100"
                        style={{ height: 90 }}
                        onClick={() => ref.current.click()}
                        disabled={dangXuLy === mat}
                    >
                        {dangXuLy === mat ? "Đang xử lý..." : "Tải ảnh lên"}
                    </button>
                ) : (
                    <div className="border rounded d-flex align-items-center justify-content-center text-muted"
                        style={{ height: 90, fontSize: 13 }}>
                        Chưa có ảnh
                    </div>
                )}
                <input
                    type="file"
                    accept="image/*"
                    ref={ref}
                    style={{ display: "none" }}
                    onChange={(e) => chonAnh(mat, e)}
                />
            </div>
        );
    };

    return (
        <>
            <div className="col-12">
                <label className="mb-1"><strong>Ảnh căn cước công dân</strong></label>
            </div>
            {renderO("front", "Mặt trước")}
            {renderO("back", "Mặt sau")}
            {loi && (
                <div className="col-12">
                    <div className="alert alert-danger py-1 px-2"><small>{loi}</small></div>
                </div>
            )}

            <Modal show={!!xemAnh} onHide={dongXem} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">Ảnh căn cước — {xemAnh?.nhan}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-2">
                    {xemAnh?.dangTai && <div className="py-4"><span className="spinner-border text-primary"></span></div>}
                    {xemAnh?.loi && <div className="alert alert-danger mb-0">{xemAnh.loi}</div>}
                    {xemAnh?.url && (
                        <img src={xemAnh.url} alt={xemAnh.nhan} style={{ maxWidth: "100%", maxHeight: "75vh" }} />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}
